import React from 'react';
import { createPortal } from 'react-dom';
import { Clock, MapPin, X, Edit, Trash2, Share2, Clipboard, StickyNote, CheckCircle2, Square, Calendar } from 'lucide-react';

const ICONS = {
    calendar: "https://raw.githubusercontent.com/Tarikul-Islam-Anik/Animated-Fluent-Emojis/master/Emojis/Objects/Calendar.png",
    memo: "https://raw.githubusercontent.com/Tarikul-Islam-Anik/Animated-Fluent-Emojis/master/Emojis/Objects/Memo.png",
    pushpin: "https://raw.githubusercontent.com/Tarikul-Islam-Anik/Animated-Fluent-Emojis/master/Emojis/Objects/Pushpin.png",
    check: "https://raw.githubusercontent.com/Tarikul-Islam-Anik/Animated-Fluent-Emojis/master/Emojis/Symbols/Check%20Mark%20Button.png",
    bible: "https://raw.githubusercontent.com/Tarikul-Islam-Anik/Animated-Fluent-Emojis/master/Emojis/Objects/Books.png", // Worship
    note: "https://raw.githubusercontent.com/Tarikul-Islam-Anik/Animated-Fluent-Emojis/master/Emojis/Objects/Spiral%20Notepad.png", // Meeting
    coffee: "https://raw.githubusercontent.com/Tarikul-Islam-Anik/Animated-Fluent-Emojis/master/Emojis/Food/Hot%20Beverage.png", // Fellowship
    balloon: "https://raw.githubusercontent.com/Tarikul-Islam-Anik/Animated-Fluent-Emojis/master/Emojis/Activities/Balloon.png", // Event
    cake: "https://raw.githubusercontent.com/Tarikul-Islam-Anik/Animated-Fluent-Emojis/master/Emojis/Food/Birthday%20Cake.png", // Birthday
    fire: "https://raw.githubusercontent.com/Tarikul-Islam-Anik/Animated-Fluent-Emojis/master/Emojis/Travel%20and%20places/Fire.png" // D-Day High Priority
};

const getTypeIcon = (type) => {
    switch (type) {
        case 'worship': return ICONS.bible;
        case 'event': return ICONS.balloon;
        case 'meeting': return ICONS.note;
        case 'fellowship': return ICONS.coffee;
        default: return ICONS.pushpin;
    }
};

const getTypeName = (type) => {
    switch (type) {
        case 'worship': return '예배';
        case 'event': return '행사';
        case 'meeting': return '회의';
        case 'fellowship': return '교제';
        case 'etc': return '기타';
        default: return '기타';
    }
};

const getDepartmentColor = (dept, userProfile) => {
    if (!dept) return 'bg-gray-500';

    // 1. Check if it matches User's Main Department
    if (userProfile && (userProfile.deptName === dept || (dept.includes(userProfile.deptName))) && userProfile.deptColor) {
        return userProfile.deptColor;
    }

    if (dept.includes('유아')) return 'bg-pink-400';
    if (dept.includes('유치')) return 'bg-yellow-400';
    if (dept.includes('유년')) return 'bg-orange-400';
    if (dept.includes('초등')) return 'bg-sky-500';
    if (dept.includes('중등')) return 'bg-lime-500';
    if (dept.includes('고등')) return 'bg-teal-500';
    if (dept.includes('청년')) return 'bg-indigo-500';
    if (dept.includes('장년')) return 'bg-purple-600';
    return 'bg-gray-500'; // 그외
};

const renderPriorityBadge = (priority) => {
    switch (priority) {
        case 'high': return <span className="text-[10px] font-bold text-red-500 bg-red-50 px-2 py-0.5 rounded-full border border-red-100">긴급</span>;
        case 'low': return <span className="text-[10px] font-bold text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full border border-gray-200">여유</span>;
        default: return null;
    }
};

export default function ScheduleDetailModal({ schedule, onClose, onEdit, onDelete, onShare, onChecklistToggle, theme, userProfile }) {
    if (!schedule) return null;
    const s = schedule;
    const progress = s.checklists?.length > 0
        ? Math.round((s.checklists.filter(c => c.checked).length / s.checklists.length) * 100)
        : 0;

    return createPortal(
        <div className="fixed inset-0 flex items-center justify-center z-[9999] p-4" onClick={onClose}>
            <div className="bg-[#FAFAFA]/95 backdrop-blur-2xl rounded-[2.5rem] w-full max-w-md relative max-h-[85vh] overflow-y-auto shadow-2xl custom-scrollbar border border-white/60" onClick={e => e.stopPropagation()}>

                {/* Gradient Header */}
                <div className={`h-36 relative overflow-hidden ${s.type === 'worship' ? 'bg-gradient-to-br from-purple-100 to-purple-50' : s.type === 'event' ? 'bg-gradient-to-br from-orange-100 to-orange-50' : s.type === 'meeting' ? 'bg-gradient-to-br from-blue-100 to-blue-50' : 'bg-gradient-to-br from-gray-100 to-gray-50'}`}>
                    {/* Abstract Shapes for Depth */}
                    <div className="absolute top-[-20%] right-[-10%] w-40 h-40 rounded-full bg-white/20 blur-2xl"></div>
                    <div className="absolute bottom-[-10%] left-[-10%] w-32 h-32 rounded-full bg-white/30 blur-2xl"></div>

                    <button onClick={onClose} className="absolute top-5 right-5 p-2.5 bg-white/40 hover:bg-white/80 active:scale-95 backdrop-blur-md rounded-full text-gray-600 transition shadow-sm border border-white/20 z-10">
                        <X size={20} />
                    </button>
                </div>

                {/* Content Header (Floating Avatar) */}
                <div className="px-8 relative -mt-14 mb-6">
                    <div className="flex justify-between items-end">
                        <div className="w-28 h-28 rounded-[2rem] bg-white shadow-[0_8px_30px_rgba(0,0,0,0.08)] flex items-center justify-center border-4 border-white/80 rotate-[-3deg] hover:rotate-0 transition-transform duration-300">
                            <img src={getTypeIcon(s.type)} className="w-16 h-16 object-contain drop-shadow-md" alt="icon" />
                        </div>

                        {/* Actions (Floating Pills) */}
                        <div className="flex gap-2 mb-2">
                            <button onClick={() => onShare(s)} className="p-3 bg-white text-gray-600 rounded-2xl shadow-[0_4px_12px_rgba(0,0,0,0.05)] hover:shadow-md hover:-translate-y-0.5 active:scale-95 transition border border-gray-50">
                                <Share2 size={18} />
                            </button>
                            <button onClick={() => onEdit(s)} className={`px-5 py-3 ${theme.bg} text-white text-sm font-bold rounded-2xl shadow-[0_8px_20px_rgba(53,79,56,0.3)] hover:shadow-xl hover:-translate-y-0.5 active:scale-95 transition flex items-center gap-2`}>
                                <Edit size={16} /> 수정
                            </button>
                        </div>
                    </div>

                    <div className="mt-5 space-y-2">
                        <div className="flex items-center gap-2">
                            <span className={`px-3 py-1 rounded-full text-[11px] font-bold text-white shadow-sm ${getDepartmentColor(s.department || 'All', userProfile)}`}>
                                {s.department === 'All' || !s.department ? '전체' : s.department}
                            </span>
                            <span className={`px-3 py-1 rounded-full text-[11px] font-bold text-white shadow-sm ${s.type === 'worship' ? 'bg-purple-500' : s.type === 'event' ? 'bg-orange-500' : s.type === 'meeting' ? 'bg-blue-500' : 'bg-gray-500'}`}>
                                {getTypeName(s.type)}
                            </span>
                            {renderPriorityBadge(s.priority)}
                        </div>
                        <h2 className={`text-3xl font-black ${theme.text} leading-tight font-do-hyeon drop-shadow-sm ${s.isCompleted ? 'line-through opacity-50' : ''}`}>
                            {s.title}
                        </h2>
                        <p className="text-sm font-bold text-gray-400 flex items-center gap-1">
                            <Calendar size={14} /> {s.date}
                        </p>
                    </div>
                </div>

                {/* Body Content (Floating Cards) */}
                <div className="px-8 pb-10 space-y-6">

                    {/* Time & Location */}
                    <div className="grid grid-cols-1 gap-4">
                        {(s.startTime || s.endTime) && (
                            <div className="flex items-center gap-4 p-4 rounded-[2rem] bg-white shadow-[0_4px_20px_rgba(0,0,0,0.03)] border border-transparent hover:border-blue-100 transition group">
                                <div className="p-3 bg-blue-50 rounded-2xl text-blue-500 shadow-inner group-hover:scale-110 transition-transform"><Clock size={20} /></div>
                                <div>
                                    <p className="text-[10px] text-gray-400 font-bold mb-0.5 uppercase tracking-wider">Time</p>
                                    <p className="text-base font-black text-gray-800">{s.startTime || ""}{s.startTime && s.endTime ? " - " : ""}{s.endTime || ""}</p>
                                </div>
                            </div>
                        )}
                        {s.location && (
                            <div className="flex items-center gap-4 p-4 rounded-[2rem] bg-white shadow-[0_4px_20px_rgba(0,0,0,0.03)] border border-transparent hover:border-red-100 transition group">
                                <div className="p-3 bg-red-50 rounded-2xl text-red-500 shadow-inner group-hover:scale-110 transition-transform"><MapPin size={20} /></div>
                                <div>
                                    <p className="text-[10px] text-gray-400 font-bold mb-0.5 uppercase tracking-wider">Location</p>
                                    <p className="text-base font-black text-gray-800">{s.location}</p>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Checklists */}
                    {s.checklists && s.checklists.length > 0 && (
                        <div className="space-y-4">
                            <div className="flex justify-between items-center px-1">
                                <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider flex items-center gap-2">
                                    <CheckCircle2 size={14} /> Checklist
                                </h4>
                                <span className={`text-[10px] font-black ${theme.text} ${theme.lightBg} px-3 py-1 rounded-full`}>{progress}% Done</span>
                            </div>

                            <div className="bg-white p-5 rounded-[2rem] shadow-[0_4px_20px_rgba(0,0,0,0.03)] space-y-4">
                                {/* Progress Bar 3D */}
                                <div className="w-full bg-gray-100 rounded-full h-3 shadow-inner overflow-hidden">
                                    <div className={`bg-gradient-to-r from-${theme.bg.replace("bg-", "")} to-[#4A6F4E] h-full rounded-full transition-all duration-700 ease-out shadow-md`} style={{ width: `${progress}%` }}></div>
                                </div>

                                <div className="space-y-2">
                                    {s.checklists.map(item => (
                                        <div key={item.id} onClick={() => onChecklistToggle(s, item.id)} className="flex items-center gap-3 p-3 hover:bg-gray-50 rounded-2xl cursor-pointer transition group">
                                            <div className={`transition-transform duration-200 ${item.checked ? `${theme.text} scale-100` : 'text-gray-300 scale-90 group-hover:scale-100'}`}>
                                                {item.checked ? <CheckCircle2 size={20} className={`${theme.text.replace("text-", "fill-")}/10`} /> : <Square size={20} />}
                                            </div>
                                            <span className={`text-sm font-bold transition-colors ${item.checked ? 'text-gray-400 line-through' : 'text-gray-700'}`}>{item.text}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Memo */}
                    {s.description && (
                        <div className="space-y-3">
                            <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider px-1 flex items-center gap-2">
                                <StickyNote size={14} /> Memo
                            </h4>
                            <div className="p-6 rounded-[2rem] bg-[#fffdef] shadow-[0_4px_15px_rgba(0,0,0,0.02)] border border-yellow-100/50 relative overflow-hidden">
                                <div className="absolute top-0 right-0 w-8 h-8 bg-gradient-to-bl from-yellow-200/50 to-transparent"></div>
                                <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap font-medium relative z-10">
                                    {s.description}
                                </p>
                            </div>
                        </div>
                    )}

                    {/* Delete Button */}
                    <div className="pt-6 flex justify-center">
                        <button onClick={() => onDelete(s.id)} className="text-xs text-red-400 hover:text-red-500 font-bold flex items-center gap-2 px-6 py-3 rounded-2xl hover:bg-red-50 transition">
                            <Trash2 size={14} /> 일정 삭제하기
                        </button>
                    </div>

                </div>
            </div>
        </div>,
        document.body
    );
}
